import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { EmailSchema } from '../../../utils/schema';
import { SimpleLayout } from '../../layouts/simple-layout';
import { BaseInput } from '@components/molecules';
import type { ResponseResult } from '@utils';
import { WithControl } from '@utils';

export type SelectEmailFormLabels = {
	title: string;
	subtitle: string;
	submit: string;
	noLinkButton: string;
	label: string;
};

type Props = {
	labels: SelectEmailFormLabels;
	onSubmit: (data: { email: string }) => Promise<ResponseResult<any>>;
	goBack?: () => void;
	defaultEmail?: string;
	initialError?: string;
	isLoading?: boolean;
};

const schema = z.object({
	email: EmailSchema,
});

export const ChooseEmailForm: React.FC<Props> = ({
	labels,
	onSubmit,
	goBack,
	defaultEmail,
	initialError,
	isLoading = false,
}) => {
	const {
		control,
		handleSubmit,
		formState: { isValid, isSubmitting, errors },
	} = useForm({
		defaultValues: {
			email: defaultEmail?.trim() ?? '',
		},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});

	const [requestError, setRequestError] = React.useState<string | undefined>(initialError);

	React.useEffect(() => {
		setRequestError(initialError);
	}, [initialError]);

	const ControlledEmailInput = WithControl(BaseInput, control);

	const handleOnSubmit = async (values: z.infer<typeof schema>) => {
		setRequestError(undefined);
		const { error } = await onSubmit(values);

		if (error) {
			setRequestError(error.message);
		}
	};

	const isBusy = isSubmitting || isLoading;

	return (
		<SimpleLayout
			title={labels.title}
			subtitle={labels.subtitle}
			submitButton={labels.submit}
			linkButton={labels.noLinkButton}
			handleSubmit={handleSubmit(handleOnSubmit)}
			isValid={isValid && !isBusy}
			isLoading={isBusy}
			onLinkClick={goBack}
			error={requestError}
		>
			<div className="flex flex-col gap-2.5">
				<ControlledEmailInput
					name="email"
					type="email"
					label={labels.label}
					error={errors.email?.message}
					data-aid="code-input"
				/>
			</div>
		</SimpleLayout>
	);
};
